/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 데이터 프로바이더 공통 클래스를 정의한다.
 *
 * @file /src/models/store/Provider.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 8. 11.
 */
import { Stores } from '@models/store/Store';
import { Record } from '@models/store/Record';

export interface Properties {
    /**
     * 로딩 매개변수
     */
    params?: { [key: string]: any };

    /**
     * 페이지당 가져올 갯수
     */
    limit?: number;

    /**
     * 가져올 페이지 번호 또는 페이지에 포함될 Record 또는 data
     */
    page?: number | Record | { [key: string]: any };

    /**
     * 로딩을 위한 매개변수 빌더
     */
    paramBuilder?: () => Promise<{ [key: string]: any }>;

    /**
     * 데이터 로더
     */
    loader?: () => Promise<{ [key: string]: any }[]>;

    /**
     * 자식 데이터 로더
     */
    childrenLoader?: (parent: Record) => Promise<{ [key: string]: any }[]>;

    /**
     * 자식 데이터 프로바이더
     */
    childrenProvider?: (parent: Record) => { url: string; params: { [key: string]: any } };
}

export abstract class Provider {
    /**
     * 원격주소
     */
    url: string = null;

    /**
     * 로딩 매개변수
     */
    params: { [key: string]: any } = null;

    /**
     * 페이지당 가져올 갯수
     */
    limit: number;

    /**
     * 가져올 페이지 번호 또는 페이지에 포함될 Record 또는 data
     */
    page?: number | Record | { [key: string]: any };

    /**
     * 총 페이지 번호
     */
    total: number;

    /**
     * 데이터로딩여부
     */
    loaded: boolean = false;

    /**
     * 데이터 갱신시각
     */
    updatedAt: number;

    /**
     * 데이터 로더
     */
    loader: () => Promise<{ [key: string]: any }[]>;

    /**
     * 데이터 로더
     */
    childrenLoader: (parent: Record) => Promise<{ [key: string]: any }[]>;

    /**
     * 자식 데이터 프로바이더
     */
    childrenProvider: (parent: Record) => { url?: string; params?: { [key: string]: any } };

    /**
     * 데이터스토어를 정의한다.
     *
     * @param store - 데이터스토어 인스턴스
     * @param properties - 초기속성
     */
    constructor(
        public store: Stores,
        public properties: Properties = null
    ) {
        this.params = this.properties?.params ?? {};
        this.limit = this.properties?.limit ?? 0;
        this.page = this.properties?.page ?? 1;
        this.loader = this.properties?.loader ?? null;
        this.childrenLoader = this.properties?.childrenLoader ?? null;
        this.childrenProvider = this.properties?.childrenProvider ?? null;
    }

    /**
     * 데이터를 불러오기 위한 URL 을 설정한다.
     *
     * @param url - URL
     */
    setUrl(url: string): void {
        this.url = url;
    }

    /**
     * 데이터를 불러오기 위한 매개변수를 설정한다.
     *
     * @param params - 매개변수
     */
    setParams(params: { [key: string]: any }): void {
        for (const key in params) {
            this.setParam(key, params[key]);
        }
    }

    /**
     * 데이터를 불러오기 위한 매개변수를 설정한다.
     *
     * @param key - 매개변수명
     * @param value - 매개변수값
     */
    setParam(key: string, value: any): void {
        this.params ??= {};
        this.params[key] = value;
    }

    /**
     * 매개변수를 가져온다.
     *
     * @returns params - 매개변수
     */
    getParams(): { [key: string]: any } {
        return { ...(this.params ?? {}) };
    }

    /**
     * 데이터를 불러오기 위한 매개변수를 가져온다.
     *
     * @param key - 매개변수명
     * @returns 매개변수값
     */
    getParam(key: string): any {
        return this.getParams()[key] ?? null;
    }

    /**
     * 현재페이지를 가져온다.
     *
     * @returns 현재페이지
     */
    getPage(): number {
        if (typeof this.page == 'number') {
            return Math.max(1, this.page ?? 1);
        } else {
            return 1;
        }
    }

    /**
     * 페이지를 설정한다.
     *
     * @param page - 불러올 페이지
     */
    setPage(page: number | Record | { [key: string]: any }): void {
        this.page = page;
    }

    /**
     * 전체페이지를 가져온다.
     *
     * @return 전체페이지
     */
    getTotalPage(): number {
        return this.limit > 0 ? Math.max(1, Math.ceil(this.total / this.limit)) : 1;
    }

    /**
     * 데이터 갱신시각을 가져온다.
     *
     * @returns 데이터 갱신시각
     */
    getUpdatedAt(): number {
        return this.updatedAt ?? Math.round(new Date().getTime() / 1000);
    }

    /**
     * 데이터스토어를 다시 불러올 수 있는 상태인지 확인한다.
     *
     * @returns 리로드가능여부
     */
    isLoadable(): boolean {
        return this.loaded === false;
    }

    /**
     * 데이터를 로딩했는지 여부를 가져온다.
     *
     * @returns 데이터 로딩여부
     */
    isLoaded(): boolean {
        return this.loaded;
    }

    /**
     * 데이터스토어가 원격스토어인지 확인한다.
     *
     * @returns 원격스토어여부
     */
    isRemote(): boolean {
        return this.url !== null;
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.store = null;
        this.loader = null;
        this.childrenLoader = null;
        this.childrenProvider = null;
        this.properties = null;
    }

    /**
     * 데이터를 불러오기 위한 매개변수를 가져온다.
     *
     * @returns 매개변수
     */
    abstract getLoaderParams(): Promise<{ [key: string]: any }>;

    /**
     * 데이터를 불러온다.
     */
    abstract load(): Promise<void>;

    /**
     * 데이터 로더를 초기화한다.
     */
    abstract reset(): Promise<void>;

    /**
     * 변경된 데이터를 커밋한다.
     * @param is_all
     */
    abstract commit(is_all: boolean): Promise<boolean>;
}
